import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  TablePagination,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  RestartAlt as ResetIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { useNavigate } from 'react-router-dom';
import { ZulassungsantragService } from '../services/zulassungsantragService';
import { Zulassungsantrag, ZulassungsantragResponse, ZulassungsantragListItem, GeschaeftsvorfallTyp, AntragstellerTyp, ZulassungsantragFormData, getGeschaeftsvorfallTypLabel, getAntragstellerTypLabel } from '../types/antrag';
import { useAuth } from '../context/AuthContext';
import { NutzerService } from '../services/nutzerService';
import { Nutzer } from '../types/nutzer';

const AntraegePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [antrag, setAntrag] = useState<ZulassungsantragListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGeschaeftsvorfallTyp, setSelectedGeschaeftsvorfallTyp] = useState<string>('NEUZULASSUNG');
  const [selectedAntragstellerTyp, setSelectedAntragstellerTyp] = useState<string>('EIGENE_PERSON');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [nutzerDetails, setNutzerDetails] = useState<Nutzer | null>(null);
  const [finInput, setFinInput] = useState('');
  const [kennzeichenInput, setKennzeichenInput] = useState('');
  const [activeFinFilter, setActiveFinFilter] = useState<string | undefined>(undefined);
  const [activeKennzeichenFilter, setActiveKennzeichenFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetConfirmAntragId, setResetConfirmAntragId] = useState<number | null>(null);

  /*
  // Form State
  const [formData, setFormData] = useState<NutzerCreateRequest>({
    username: '',
    email: '',
    vorname: '',
    nachname: '',
    passwordHash: '',
    kundeId: 0
  });
*/
  useEffect(() => {
    loadAntraege();
    loadNutzerDetails();
  }, [page, rowsPerPage, activeFinFilter, activeKennzeichenFilter, statusFilter]);

  const loadNutzerDetails = async () => {
    if (!user?.id) return;
    try {
      const nutzer = await NutzerService.getNutzerById(user.id);
      setNutzerDetails(nutzer);
    } catch (err) {
      console.error('Fehler beim Laden der Nutzerdaten:', err);
    }
  };

  const loadAntraege = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ZulassungsantragService.getAlleZulassungsantraege(page, rowsPerPage, 'id', 'desc', activeFinFilter, activeKennzeichenFilter, statusFilter || undefined);
      setAntrag(response.content);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Fehler beim Laden der Anträge:', err);
      setError('Fehler beim Laden der Anträge');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setActiveFinFilter(finInput.trim() || undefined);
    setActiveKennzeichenFilter(kennzeichenInput.trim() || undefined);
  };

  const handleClearSearch = () => {
    setFinInput('');
    setKennzeichenInput('');
    setPage(0);
    setActiveFinFilter(undefined);
    setActiveKennzeichenFilter(undefined);
    setStatusFilter('');
  };

  const hasActiveFilter = !!(activeFinFilter || activeKennzeichenFilter || statusFilter);

  const handleViewDetails = (antragId: number) => {
    navigate(`/zulassungsantraege/${antragId}`);
  };

  const handleOpenDialog = async () => {
    // Sicherstellen, dass Nutzerdaten geladen sind
    if (!nutzerDetails) {
      await loadNutzerDetails();
    }
    
    // Prüfen ob Kunde vorhanden ist
    if (!nutzerDetails?.kunde && !user?.id) {
      setError('Fehler: Keine Benutzer- oder Kundenzuordnung gefunden. Bitte melden Sie sich erneut an.');
      return;
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedGeschaeftsvorfallTyp('NEUZULASSUNG');
    setSelectedAntragstellerTyp('EIGENE_PERSON');
  };

  const handleCreateNewAntrag = async () => {
    // Nochmals Nutzerdaten laden, falls noch nicht vorhanden
    let currentNutzerDetails = nutzerDetails;
    if (!currentNutzerDetails && user?.id) {
      try {
        currentNutzerDetails = await NutzerService.getNutzerById(user.id);
        setNutzerDetails(currentNutzerDetails);
      } catch (err) {
        console.error('Fehler beim Laden der Nutzerdaten:', err);
        setError('Fehler: Nutzerdaten konnten nicht geladen werden');
        return;
      }
    }

    if (!currentNutzerDetails?.kunde) {
      setError('Fehler: Keine Kundenzuordnung gefunden. Dieser Nutzer ist keinem Kunden zugeordnet.');
      return;
    }

    console.log('Erstelle Antrag mit Kunde:', currentNutzerDetails.kunde);
    console.log('Nutzer ID:', user?.id);

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Minimalen Antrag mit Kunde, Nutzer und Geschäftsvorfall erstellen
      const minimalFormData: ZulassungsantragFormData = {
        geschaeftsvorfallTyp: selectedGeschaeftsvorfallTyp,
        antragstellerTyp: selectedAntragstellerTyp,
        kundeId: currentNutzerDetails.kunde.id,
        nutzerId: user!.id,
        kundenNummer: currentNutzerDetails.kunde.kundenNummer,
        status: 'ERSTELLT',
        
        // Halterdaten - Platzhalter
        halterTyp: 'natuerlich',
        vorname: '',
        nachname: '',
        
        // Fahrzeugdaten - Platzhalter
        fahrzeugTyp: 'auto',
        fin: '',
        zbIINummer: '',
        zbIISicherheitscode: '',
        
        // Bankverbindung - Platzhalter
        zulassungsstelle: '',
        iban: '',
        bankName: '',
        
        // Versandadresse - Platzhalter
        versandName: '',
        versandStrasse: '',
        versandHausnummer: '',
        versandPostleitzahl: '',
        versandOrt: '',
        
        // Alte Felder - Platzhalter
        gkIdZulB: '',
        kennzeichen: '',
        vbNummer: '',
        berufsschluessel: ''
      };

      // Antrag erstellen
      const response = await ZulassungsantragService.createZulassungsantrag(minimalFormData);
      
      setSuccess(`Antrag erfolgreich erstellt (ID: ${response.id})`);
      handleCloseDialog();
      
      // Zur Detailseite navigieren
      navigate(`/zulassungsantraege/${response.id}`);
    } catch (err: any) {
      console.error('Fehler beim Erstellen des Antrags:', err);
      setError(`Fehler beim Erstellen des Antrags: ${err.response?.data?.message || err.message}`);
      setLoading(false);
    }
  };

  const handleSendToGKS = async (antragId: number) => {
    if (window.confirm('Möchten Sie diesen Antrag wirklich an das KBA senden?')) {
      try {
        setLoading(true);
        setError(null);
        const result = await ZulassungsantragService.submitZulassungsantrag(antragId);
        await loadAntraege(); // Tabelle neu laden
        
        if (result.quittung?.success) {
          setSuccess(`Antrag erfolgreich gesendet! ${result.quittung.antragsnummer ? `KBA-Nr: ${result.quittung.antragsnummer}` : ''}`);
        } else if (result.quittung?.fehlerMeldungen && result.quittung.fehlerMeldungen.length > 0) {
          setError(`KBA-Fehler: ${result.quittung.fehlerMeldungen.join(', ')}`);
        } else {
          setSuccess('Antrag wurde gesendet');
        }
      } catch (err: any) {
        console.error('Fehler beim Senden des Antrags:', err);
        setError(err.response?.data?.message || 'Fehler beim Senden des Antrags an das KBA');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReset = (antragId: number) => {
    setResetConfirmAntragId(antragId);
    setResetConfirmOpen(true);
  };

  const handleResetConfirmed = async () => {
    if (resetConfirmAntragId === null) return;
    setResetConfirmOpen(false);
    try {
      setLoading(true);
      setError(null);
      await ZulassungsantragService.resetZulassungsantrag(resetConfirmAntragId);
      setSuccess('Antrag erfolgreich zurückgesetzt');
      await loadAntraege();
    } catch (err) {
      console.error('Fehler beim Zurücksetzen des Antrags:', err);
      setError('Fehler beim Zurücksetzen des Antrags');
    } finally {
      setLoading(false);
      setResetConfirmAntragId(null);
    }
  };

  const handleSign = async (antragId: number) => {
    if (window.confirm('Möchten Sie diesen Antrag wirklich unterschreiben?')) {
      try {
        setLoading(true);
        setError(null);
        await ZulassungsantragService.signZulassungsantrag(antragId);
        setSuccess('Antrag erfolgreich unterschrieben');
        await loadAntraege(); // Tabelle neu laden
      } catch (err) {
        console.error('Fehler beim Unterschreiben des Antrags:', err);
        setError('Fehler beim Unterschreiben des Antrags');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (antragId: number) => {
    if (window.confirm('Möchten Sie diesen Antrag wirklich löschen?')) {
      try {
        setLoading(true);
        setError(null);
        await ZulassungsantragService.deleteZulassungsantrag(antragId);
        setSuccess('Antrag erfolgreich gelöscht');
        await loadAntraege(); // Tabelle neu laden
      } catch (err: any) {
        console.error('Fehler beim Löschen des Antrags:', err);
        setError(err.response?.data?.message || 'Fehler beim Löschen des Antrags');
      } finally {
        setLoading(false);
      }
    }
  }
  /*
  const handleOpenDialog = (nutzer?: Nutzer) => {
    if (nutzer) {
      setEditingNutzer(nutzer);
      setFormData({
        username: nutzer.username,
        email: nutzer.email,
        vorname: nutzer.vorname,
        nachname: nutzer.nachname,
        passwordHash: '', // Password nicht vorausfüllen
        kundeId: nutzer.kunde.id
      });
    } else {
      setEditingNutzer(null);
      setFormData({
        username: '',
        email: '',
        vorname: '',
        nachname: '',
        passwordHash: '',
        kundeId: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNutzer(null);
    setFormData({
      username: '',
      email: '',
      vorname: '',
      nachname: '',
      passwordHash: '',
      kundeId: 0
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingNutzer) {
        // Update existing nutzer
        await NutzerService.updateNutzer(editingNutzer.id, {
          username: formData.username,
          email: formData.email,
          vorname: formData.vorname,
          nachname: formData.nachname,
          kundeId: formData.kundeId
        });
      } else {
        // Create new nutzer
        await NutzerService.createNutzer(formData);
      }
      
      handleCloseDialog();
      loadNutzer();
    } catch (err) {
      console.error('Fehler beim Speichern des Nutzers:', err);
      setError('Fehler beim Speichern des Nutzers');
    }
  };

  const handleDelete = async (nutzer: Nutzer) => {
    if (window.confirm(`Möchten Sie den Nutzer "${nutzer.username}" wirklich löschen?`)) {
      try {
        await NutzerService.deleteNutzer(nutzer.id);
        loadNutzer();
      } catch (err) {
        console.error('Fehler beim Löschen des Nutzers:', err);
        setError('Fehler beim Löschen des Nutzers');
      }
    }
  };
*/
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status-Farben für Chips
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'ERSTELLT':
        return 'default';
      case 'GESENDET':
        return 'info';
      case 'IN_BEARBEITUNG':
        return 'primary';
      case 'GEPRUEFT':
        return 'secondary';
      case 'ABGESCHLOSSEN':
        return 'success';
      case 'ABGELEHNT':
        return 'error';
      case 'FEHLER':
        return 'error';
      default:
        return 'default';
    }
  };

  // Status-Label für bessere Lesbarkeit
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'ERSTELLT':
        return 'Erstellt';
      case 'GESENDET':
        return 'Gesendet';
      case 'IN_BEARBEITUNG':
        return 'In Bearbeitung';
      case 'GEPRUEFT':
        return 'Geprüft';
      case 'ABGESCHLOSSEN':
        return 'Abgeschlossen';
      case 'ABGELEHNT':
        return 'Abgelehnt';
      case 'QES_PROZESS':
        return 'QES Prozess';
      case 'QES_SIGNIERT':
        return 'QES Signiert';
      case 'FEHLER':
        return 'Fehler';
      default:
        return status;
    }
  };
/*
  const isFormValid = () => {
    return formData.username && 
           formData.email && 
           formData.vorname && 
           formData.nachname && 
           formData.kundeId > 0 &&
           (!editingNutzer ? formData.passwordHash : true); // Password nur bei neuen Nutzern erforderlich
  };
*/
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Zulassungsanträge
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ mr: 1 }}
          >
            Neuer Antrag
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadAntraege}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Suche nach FIN, Kennzeichen und Status */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="FIN"
            placeholder="z.B. WVW oder WVWZZZ..."
            value={finInput}
            onChange={(e) => setFinInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            inputProps={{ maxLength: 17, style: { fontFamily: 'monospace' } }}
            InputProps={finInput ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setFinInput(''); setActiveFinFilter(undefined); setPage(0); }}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              )
            } : undefined}
            sx={{ width: 220 }}
          />
          <TextField
            size="small"
            label="Kennzeichen"
            placeholder="z.B. DA FG 333"
            value={kennzeichenInput}
            onChange={(e) => setKennzeichenInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            inputProps={{ style: { fontFamily: 'monospace' } }}
            InputProps={kennzeichenInput ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setKennzeichenInput(''); setActiveKennzeichenFilter(undefined); setPage(0); }}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              )
            } : undefined}
            sx={{ width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Alle Status</MenuItem>
              <MenuItem value="ERSTELLT">Erstellt</MenuItem>
              <MenuItem value="GESENDET">Gesendet</MenuItem>
              <MenuItem value="IN_BEARBEITUNG">In Bearbeitung</MenuItem>
              <MenuItem value="GEPRUEFT">Geprüft</MenuItem>
              <MenuItem value="ABGESCHLOSSEN">Abgeschlossen</MenuItem>
              <MenuItem value="ABGELEHNT">Abgelehnt</MenuItem>
              <MenuItem value="QES_PROZESS">QES Prozess</MenuItem>
              <MenuItem value="QES_SIGNIERT">QES Signiert</MenuItem>
              <MenuItem value="FEHLER">Fehler</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={handleSearch}>
            Suchen
          </Button>
          {hasActiveFilter && (
            <Button size="small" startIcon={<ClearIcon />} onClick={handleClearSearch} color="inherit">
              Filter löschen
            </Button>
          )}
          {activeFinFilter && <Chip label={`FIN: ${activeFinFilter}`} size="small" color="primary" variant="outlined" onDelete={() => { setActiveFinFilter(undefined); setFinInput(''); setPage(0); }} />}
          {activeKennzeichenFilter && <Chip label={`Kennzeichen: ${activeKennzeichenFilter}`} size="small" color="primary" variant="outlined" onDelete={() => { setActiveKennzeichenFilter(undefined); setKennzeichenInput(''); setPage(0); }} />}
          {statusFilter && <Chip label={`Status: ${getStatusLabel(statusFilter)}`} size="small" color="primary" variant="outlined" onDelete={() => { setStatusFilter(''); setPage(0); }} />}
        </Box>
      </Paper>

      <Paper elevation={3}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Haltername</TableCell>
                <TableCell>Vorfalltyp</TableCell>
                <TableCell>FIN</TableCell>
                <TableCell>Kennzeichen</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : antrag.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Keine Anträge gefunden
                  </TableCell>
                </TableRow>
              ) : (
                antrag.map((antrag) => (
                  <TableRow key={antrag.id} hover>
                    <TableCell>
                      <Chip label={antrag.id} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(antrag.status)} 
                        color={getStatusColor(antrag.status)}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      {antrag.halterName || 'Kein Halter'}
                    </TableCell>
                    <TableCell>
                      {getGeschaeftsvorfallTypLabel(antrag.geschaeftsvorfallTyp)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {antrag.fin || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG'
                          ? (antrag.altesKennzeichen || '-')
                          : (antrag.neuesKennzeichen || '-')}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(antrag.erstelltAm)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(antrag.id)}
                        color="primary"
                        title="Details anzeigen"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {isAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleReset(antrag.id)}
                          color="warning"
                          title="Status zurücksetzen"
                        >
                          <ResetIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
        />
      </Paper>

      {/* Dialog für neuen Antrag */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Neuen Antrag erstellen
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Geschäftsvorfall-Typ *</InputLabel>
              <Select
                value={selectedGeschaeftsvorfallTyp}
                onChange={(e) => setSelectedGeschaeftsvorfallTyp(e.target.value)}
                label="Geschäftsvorfall-Typ *"
              >
                {Object.values(GeschaeftsvorfallTyp).map((typ) => (
                  <MenuItem key={typ} value={typ}>
                    {getGeschaeftsvorfallTypLabel(typ)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Zulassung auf *</InputLabel>
              <Select
                value={selectedAntragstellerTyp}
                onChange={(e) => setSelectedAntragstellerTyp(e.target.value)}
                label="Zulassung auf *"
              >
                <MenuItem value={AntragstellerTyp.EIGENE_PERSON}>
                  {getAntragstellerTypLabel(AntragstellerTyp.EIGENE_PERSON)}
                </MenuItem>
                <MenuItem value={AntragstellerTyp.DRITTE_PERSON}>
                  {getAntragstellerTypLabel(AntragstellerTyp.DRITTE_PERSON)}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleCreateNewAntrag}
            variant="contained"
            disabled={!selectedGeschaeftsvorfallTyp}
          >
            Antrag erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bestätigungs-Dialog: Status zurücksetzen */}
      <Dialog open={resetConfirmOpen} onClose={() => { setResetConfirmOpen(false); setResetConfirmAntragId(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Status zurücksetzen</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Möchten Sie den Status dieses Antrags wirklich auf "ERSTELLT" zurücksetzen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetConfirmOpen(false); setResetConfirmAntragId(null); }}>Abbrechen</Button>
          <Button onClick={handleResetConfirmed} variant="contained" color="warning">
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AntraegePage;
